import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Loader2, Trash2, Pill, FlaskConical, FileDown, X, FileText, FileCheck2,
} from "lucide-react";
import CidAutocomplete from "@/components/CidAutocomplete";

const today = () => new Date().toISOString().slice(0, 10);

function ItemsEditor({ items, setItems, nameLabel, detailLabel, instrLabel, testidPrefix }) {
  const update = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => setItems([...items, { name: "", detail: "", instructions: "" }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border border-border rounded-md p-3 space-y-2 relative" data-testid={`${testidPrefix}-item-${i}`}>
          {items.length > 1 && (
            <button onClick={() => remove(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
              data-testid={`${testidPrefix}-remove-${i}`}><X className="w-4 h-4" /></button>
          )}
          <div className="space-y-1"><Label className="text-xs">{nameLabel}</Label>
            <Input value={it.name} onChange={(e) => update(i, "name", e.target.value)} data-testid={`${testidPrefix}-name-${i}`} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">{detailLabel}</Label>
              <Input value={it.detail} onChange={(e) => update(i, "detail", e.target.value)} data-testid={`${testidPrefix}-detail-${i}`} /></div>
            <div className="space-y-1"><Label className="text-xs">{instrLabel}</Label>
              <Input value={it.instructions} onChange={(e) => update(i, "instructions", e.target.value)} data-testid={`${testidPrefix}-instr-${i}`} /></div>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testidPrefix}-add`}>
        <Plus className="w-4 h-4 mr-2" /> Adicionar
      </Button>
    </div>
  );
}

export default function DocumentosTab({ patientId }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [examReqs, setExamReqs] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [rxOpen, setRxOpen] = useState(false);
  const [exOpen, setExOpen] = useState(false);
  const [ceOpen, setCeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [crm, setCrm] = useState("");
  const [rxItems, setRxItems] = useState([{ name: "", detail: "", instructions: "" }]);
  const [rxNotes, setRxNotes] = useState("");
  const [exItems, setExItems] = useState([{ name: "", detail: "", instructions: "" }]);
  const [exInfo, setExInfo] = useState("");
  const [ce, setCe] = useState({ days: "", cid: "", description: "", purpose: "" });

  const load = () => {
    api.get(`/patients/${patientId}/prescriptions`).then((r) => setPrescriptions(r.data));
    api.get(`/patients/${patientId}/exam-requests`).then((r) => setExamReqs(r.data));
    api.get(`/patients/${patientId}/certificates`).then((r) => setCertificates(r.data));
  };
  useEffect(() => { load(); }, [patientId]);

  const openPdf = async (path, fname) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Não foi possível gerar o PDF"); }
  };

  const saveRx = async () => {
    const items = rxItems.filter((i) => i.name.trim());
    if (!items.length) { toast.error("Adicione ao menos um medicamento"); return; }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/prescriptions`, { date: today(), items, notes: rxNotes || null, doctor_crm: crm || null });
      toast.success("Receita gerada");
      setRxOpen(false); setRxItems([{ name: "", detail: "", instructions: "" }]); setRxNotes("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveEx = async () => {
    const items = exItems.filter((i) => i.name.trim());
    if (!items.length) { toast.error("Adicione ao menos um exame"); return; }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/exam-requests`, { date: today(), items, clinical_info: exInfo || null, doctor_crm: crm || null });
      toast.success("Pedido de exame gerado");
      setExOpen(false); setExItems([{ name: "", detail: "", instructions: "" }]); setExInfo("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delDoc = async (kind, id) => {
    await api.delete(`/patients/${patientId}/${kind}/${id}`);
    load(); toast.success("Documento removido");
  };

  const saveCert = async () => {
    if (!ce.description.trim()) { toast.error("Descreva o atestado"); return; }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/certificates`, {
        date: today(), days: ce.days === "" ? null : parseInt(ce.days),
        cid: ce.cid || null, description: ce.description, purpose: ce.purpose || null, doctor_crm: crm || null,
      });
      toast.success("Atestado gerado");
      setCeOpen(false); setCe({ days: "", cid: "", description: "", purpose: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="documentos-tab">
      <div className="rounded-md border border-border bg-secondary/40 p-3 flex items-center gap-3">
        <Label className="text-sm shrink-0">CRM do médico (opcional):</Label>
        <Input value={crm} onChange={(e) => setCrm(e.target.value)} placeholder="Ex: CRM-SP 123456" className="max-w-xs" data-testid="doc-crm-input" />
      </div>

      {/* Receitas */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-medium flex items-center gap-2"><Pill className="w-5 h-5 text-ai" /> Receitas</h3>
          <Dialog open={rxOpen} onOpenChange={setRxOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-prescription-btn"><Plus className="w-4 h-4 mr-2" /> Nova receita</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="prescription-dialog">
              <DialogHeader>
                <DialogTitle>Nova receita digital</DialogTitle>
                <DialogDescription>Gera um PDF assinado com QR Code de verificação.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <ItemsEditor items={rxItems} setItems={setRxItems}
                  nameLabel="Medicamento" detailLabel="Posologia / dose" instrLabel="Instruções"
                  testidPrefix="rx" />
                <div className="space-y-2"><Label>Observações</Label>
                  <Textarea rows={2} value={rxNotes} onChange={(e) => setRxNotes(e.target.value)} data-testid="rx-notes" /></div>
              </div>
              <DialogFooter>
                <Button onClick={saveRx} disabled={saving} data-testid="save-prescription-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Gerar receita
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {prescriptions.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground border-dashed">Nenhuma receita emitida.</Card>
        ) : (
          <div className="space-y-3">
            {prescriptions.map((d) => (
              <Card key={d.id} className="p-4 border-border shadow-none flex items-center justify-between gap-4" data-testid={`prescription-${d.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{d.items?.map((i) => i.name).join(", ")}</p>
                  <p className="text-xs text-muted-foreground">{d.date} · {d.items?.length} item(ns) · {d.doctor_name}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openPdf(`/patients/${patientId}/prescriptions/${d.id}/pdf`)} data-testid={`prescription-pdf-${d.id}`}>
                    <FileDown className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => delDoc("prescriptions", d.id)} data-testid={`prescription-del-${d.id}`}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Pedidos de exame */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-medium flex items-center gap-2"><FlaskConical className="w-5 h-5 text-ai" /> Pedidos de exame</h3>
          <Dialog open={exOpen} onOpenChange={setExOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-examreq-btn"><Plus className="w-4 h-4 mr-2" /> Novo pedido</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="examreq-dialog">
              <DialogHeader>
                <DialogTitle>Nova solicitação de exames</DialogTitle>
                <DialogDescription>Gera um PDF de pedido de exames com QR Code.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <ItemsEditor items={exItems} setItems={setExItems}
                  nameLabel="Exame" detailLabel="Indicação (CID/motivo)" instrLabel="Preparo / observação"
                  testidPrefix="ex" />
                <div className="space-y-2"><Label>Informações clínicas</Label>
                  <Textarea rows={2} value={exInfo} onChange={(e) => setExInfo(e.target.value)} data-testid="ex-info" /></div>
              </div>
              <DialogFooter>
                <Button onClick={saveEx} disabled={saving} data-testid="save-examreq-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Gerar pedido
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {examReqs.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground border-dashed">Nenhum pedido emitido.</Card>
        ) : (
          <div className="space-y-3">
            {examReqs.map((d) => (
              <Card key={d.id} className="p-4 border-border shadow-none flex items-center justify-between gap-4" data-testid={`examreq-${d.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{d.items?.map((i) => i.name).join(", ")}</p>
                  <p className="text-xs text-muted-foreground">{d.date} · {d.items?.length} exame(s) · {d.doctor_name}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openPdf(`/patients/${patientId}/exam-requests/${d.id}/pdf`)} data-testid={`examreq-pdf-${d.id}`}>
                    <FileDown className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => delDoc("exam-requests", d.id)} data-testid={`examreq-del-${d.id}`}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Atestados */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-medium flex items-center gap-2"><FileCheck2 className="w-5 h-5 text-ai" /> Atestados</h3>
          <Dialog open={ceOpen} onOpenChange={setCeOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-certificate-btn"><Plus className="w-4 h-4 mr-2" /> Novo atestado</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="certificate-dialog">
              <DialogHeader>
                <DialogTitle>Novo atestado médico</DialogTitle>
                <DialogDescription>Gera um PDF de atestado com QR Code de verificação.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Dias de afastamento</Label>
                    <Input type="number" value={ce.days} onChange={(e) => setCe({ ...ce, days: e.target.value })} data-testid="ce-days" /></div>
                  <div className="space-y-1"><Label className="text-xs">CID (opcional)</Label>
                    <CidAutocomplete value={ce.cid}
                      onSelect={(code) => setCe((p) => ({ ...p, cid: code }))}
                      placeholder="Ex: J06" testid="ce-cid" /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Descrição</Label>
                  <Textarea rows={3} value={ce.description} onChange={(e) => setCe({ ...ce, description: e.target.value })}
                    placeholder="Ex: apresentou quadro gripal, necessitando repouso." data-testid="ce-description" /></div>
                <div className="space-y-1"><Label className="text-xs">Finalidade (opcional)</Label>
                  <Input value={ce.purpose} onChange={(e) => setCe({ ...ce, purpose: e.target.value })} placeholder="Ex: Trabalho / Escola" data-testid="ce-purpose" /></div>
              </div>
              <DialogFooter>
                <Button onClick={saveCert} disabled={saving} data-testid="save-certificate-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Gerar atestado
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {certificates.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground border-dashed">Nenhum atestado emitido.</Card>
        ) : (
          <div className="space-y-3">
            {certificates.map((d) => (
              <Card key={d.id} className="p-4 border-border shadow-none flex items-center justify-between gap-4" data-testid={`certificate-${d.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{d.days ? `${d.days} dia(s) de afastamento` : "Atestado"}{d.cid ? ` · CID ${d.cid}` : ""}</p>
                  <p className="text-xs text-muted-foreground truncate">{d.date} · {d.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openPdf(`/patients/${patientId}/certificates/${d.id}/pdf`)} data-testid={`certificate-pdf-${d.id}`}>
                    <FileDown className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => delDoc("certificates", d.id)} data-testid={`certificate-del-${d.id}`}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <p className="text-xs text-muted-foreground italic border-t border-border pt-4 flex items-center gap-2">
        <FileText className="w-3 h-3" /> Documentos gerados pela plataforma LIA, com QR Code de verificação. Validade conforme legislação vigente.
      </p>
    </div>
  );
}
